import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import { useTranslation } from "react-i18next";
import MediaSelectorButton from "../../components/media/MediaSelectorButton";
import SubmitButton from "../../components/ui/SubmitButton";
import {
  fetchSiteSettings,
  updateSiteSettings,
} from "../../../store/custom-actions/settings-actions/siteSettingsSlicer";
import FormField from "../../components/ui/FormField";
import SelectBox from "../../components/ui/SelectBox";
import CheckboxField from "../../components/ui/CheckboxField";

function BasicSettingsPage() {
  const { t } = useTranslation();
  const { loading: submitLoading, data: settingData } = useSelector(
    (state) => state.siteSettingsSlicer
  );
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  const textFields = [
    { label: "Site Title", name: "site_title", required: true },
    { label: "Phone Number", name: "contact_phone", required: true },
    { label: "Email", name: "contact_email", required: true },
    { label: "Address", name: "contact_address", required: true },
    {
      label: "Global Currency",
      name: "site_global_currency",
      type: "selectbox",
      required: true,
      data: [
        { id: "USD", name: "US Dollar" },
        { id: "EUR", name: "Euro" },
        { id: "EGP", name: "Egyptian Pound" },
        { id: "SAR", name: "Saudi Riyal" },
      ],
    },
    { label: "Max Orders", name: "max_order_amount", required: true },
    { label: "Min Orders", name: "min_order_amount", required: true },
    { label: "Tax amount", name: "tax_fee_amount", required: true },
    {
      label: "Amount type",
      name: "tax_fee_type",
      type: "selectbox",
      required: true,
      data: [
        { id: "fixed", name: "Fixed" },
        { id: "percentage", name: "Percentage" },
      ],
    },
    {
      label: "Guest Orders",
      name: "avilable_guest_orders",
      type: "selectbox",
      required: true,
      data: [
        { id: 1, name: "Enable" },
        { id: 0, name: "Disable" },
      ],
    },
  ];

  const socialFields = [
    { label: "Facebook URL", name: "facebook_link" },
    { label: "Instagram URL", name: "instagram_link" },
    { label: "Twitter URL", name: "twitter_link" },
    { label: "Youtube URL", name: "youtube_link" },
    { label: "TikTok URL", name: "tiktok_link" },
    { label: "Pinterest URL", name: "pinterest_link" },
    { label: "LinkedIn URL", name: "linkedin_link" },
    { label: "WhatsApp Number", name: "whatsapp_number" },
    { label: "Telegram URL", name: "telegram_link" },
    { label: "Snapchat URL", name: "snapchat_link" },
    { label: "Threads URL", name: "threads_link" },
    {
      label: "Show social media links",
      name: "show_social_links",
      type: "checkbox",
    },
  ];

  const mediaFields = [
    { label: "Site Logo", name: "site_logo" },
    { label: "Pages header", name: "page_header_img" },
    { label: "Footer logo", name: "footer_logo" },
    { label: "Invoice logo", name: "invoice_logo" },
    { label: "Favicon logo", name: "site_favicon" },
  ];

  const allFields = [...textFields, ...mediaFields, ...socialFields];

  useEffect(() => {
    allFields.forEach(({ name }) => {
      const value = settingData?.[name];
      if (typeof value === "object" && value?.id) {
        setValue(name, value.id);
      } else if (typeof value === "string" || typeof value === "number") {
        setValue(name, value);
      }
    });
  }, [settingData]);

  useEffect(() => {
    dispatch(fetchSiteSettings());
  }, [dispatch]);

  const onSubmit = async (data) => {
    await dispatch(updateSiteSettings(data));
  };

  return (
    <>
      <PageTitle
        motherMenu={"Settings"}
        activeMenu={"Basic Settings"}
        customClasses={"mb-0"}
      />
      <div className="row">
        <div className="col-xl-12 col-xxl-12">
          <form id="submit-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="p-2 mb-3 mt-0">
              <SubmitButton
                isLoading={submitLoading}
                form="submit-form"
              />
            </div>

            <div className="card">
              <h5 className="card-title p-4">{t("Basic settings")}</h5>
              <div className="card-body">
                <div className="row">
                  {textFields.map(({ label, name, type, data, required }) => (
                    <div className="col-md-6" key={name}>
                      {type === "selectbox" ? (
                        <SelectBox
                          label={label}
                          name={name}
                          register={register}
                          required={required}
                          errors={errors}
                          data={data}
                          placeholder={`Select ${label}`}
                          showLabel={true}
                        />
                      ) : (
                        <FormField
                          label={label}
                          name={name}
                          register={register}
                          required={required}
                          errors={errors}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <h5 className="card-title p-4">
                {t("Social Media Links")}
              </h5>
              <div className="card-body">
                <div className="row">
                  {socialFields.map(({ label, name, type }) => (
                    <div className="col-md-6" key={name}>
                      {type === "checkbox" ? (
                        <CheckboxField
                          label={label}
                          name={name}
                          register={register}
                          errors={errors}
                        />
                      ) : (
                        <FormField
                          label={label}
                          name={name}
                          register={register}
                          required={false}
                          errors={errors}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <h5 className="card-title p-4">{t("Settings Media")}</h5>
              <div className="card-body">
                <div className="row">
                  {mediaFields.map(({ label, name }) => (
                    <div className="col-md-6 col-lg-4" key={name}>
                      <MediaSelectorButton
                        label={label}
                        inputName={name}
                        register={register}
                        onChange={(value) => setValue(name, value)}
                        defaultSelected={
                          settingData?.[name]
                            ? [
                                {
                                  id: settingData[name].id,
                                  path: settingData[name].path,
                                },
                              ]
                            : []
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default BasicSettingsPage;
